import * as React from "react";

/**
 * App layer hub: modal stack + z-index bands.
 * Nobody hand-writes modal/chrome z-index — all derive from the constants
 * below. See `docs/Z-INDEX-LAYERS.md`.
 */

/**
 * Modal band base. Each stack layer rises one {@link MODAL_Z_INDEX_STEP}.
 * 1000 is safe only because no app global z-index exceeds 999 — Leaflet maps
 * stay in `relative z-0` wrappers. New maps must too or controls cover chrome.
 */
export const MODAL_BASE_Z_INDEX = 1000;

/** Gap between layers: backdrop, content, and room for internals. */
export const MODAL_Z_INDEX_STEP = 10;

/**
 * Chrome band ceiling (header, hamburger, fixed bars). One below the modal
 * base so chrome never ties or tops a modal.
 */
export const CHROME_MAX_Z_INDEX = MODAL_BASE_Z_INDEX - 1;

/**
 * Chrome levels, top of band downward. Each chrome file imports its level here
 * so relative order lives in one place.
 */
export const CHROME_Z_INDEX = {
  /** Open menu drawer — must beat the rest of chrome. */
  menuDrawer: CHROME_MAX_Z_INDEX,
  /** Floating cart bar. */
  cartBar: CHROME_MAX_Z_INDEX - MODAL_Z_INDEX_STEP,
  /** Fixed top header (including search portaled to `<body>`). */
  header: CHROME_MAX_Z_INDEX - MODAL_Z_INDEX_STEP * 2,
  /** Top/bottom bars: closed menu, checkout footer, /menu top bar. */
  bar: CHROME_MAX_Z_INDEX - MODAL_Z_INDEX_STEP * 3,
  /** Active-order bar, just below the menu. */
  activeOrderBar: CHROME_MAX_Z_INDEX - MODAL_Z_INDEX_STEP * 4,
} as const;

/**
 * Non-interactive decorative layers (`pointer-events: none`): order confetti and
 * `FlyToCartLayer` ghost. Above the stack, below toast.
 */
export const DECORATIVE_Z_INDEX = 1800;

/**
 * Toast. Above everything — in-modal flows report failures via toast; below the
 * stack the error would be buried. Mirrored in `--toastify-z-index` (globals.css).
 */
export const TOAST_Z_INDEX = 1999;

interface ModalStackContextValue {
  /** Ids in open order. Last is on top. */
  stack: readonly string[];
  push: (id: string) => void;
  remove: (id: string) => void;
  topId: string | null;
}

const ModalStackContext = React.createContext<ModalStackContextValue | null>(
  null,
);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [stack, setStack] = React.useState<string[]>([]);

  const push = React.useCallback((id: string) => {
    setStack((current) => (current.includes(id) ? current : [...current, id]));
  }, []);

  const remove = React.useCallback((id: string) => {
    setStack((current) => current.filter((entry) => entry !== id));
  }, []);

  const value = React.useMemo<ModalStackContextValue>(
    () => ({
      stack,
      push,
      remove,
      topId: stack.length > 0 ? stack[stack.length - 1] : null,
    }),
    [stack, push, remove],
  );

  return (
    <ModalStackContext.Provider value={value}>
      {children}
    </ModalStackContext.Provider>
  );
}

/**
 * Registers a stack layer and returns z-index from its POSITION: last opened
 * wins. Outside the provider falls back to the base (isolated tests).
 * @param open Whether the layer is visible; closed layers leave the stack.
 */
export function useModalLayer(open: boolean) {
  const context = React.useContext(ModalStackContext);
  const id = React.useId();

  const push = context?.push;
  const remove = context?.remove;

  React.useEffect(() => {
    if (!open || !push || !remove) return;
    push(id);
    return () => remove(id);
  }, [open, id, push, remove]);

  const index = context ? context.stack.indexOf(id) : -1;
  const depth = index === -1 ? 0 : index;

  const backdropZIndex = MODAL_BASE_Z_INDEX + depth * MODAL_Z_INDEX_STEP;

  return {
    /** For modal CONTENT. */
    zIndex: backdropZIndex + 1,
    /** For the BACKDROP. One level below this layer's content. */
    backdropZIndex,
    /** Is this the top modal on the stack? */
    isTop: context ? context.topId === id : true,
    /** How many modals sit below this one. */
    depth,
  };
}

/**
 * Z-index for content above the top modal: select menus, anchored dropdowns,
 * portaled poppers outside the modal DOM. Derived from stack top so a base
 * modal's popper cannot cover a later modal.
 */
export function useModalPortalZIndex() {
  const context = React.useContext(ModalStackContext);
  const depth = context ? Math.max(context.stack.length - 1, 0) : 0;
  return MODAL_BASE_Z_INDEX + depth * MODAL_Z_INDEX_STEP + 2;
}

/** Full stack access for coordinators (shortcuts, guards). */
export function useModalStack() {
  const context = React.useContext(ModalStackContext);
  return {
    stack: context?.stack ?? [],
    hasOpenModal: (context?.stack.length ?? 0) > 0,
    topId: context?.topId ?? null,
  };
}
